/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import * as yup from 'yup';

import {
  GENESYS_SMS_TYPE,
  INTELEPEER_SMS_TYPE,
  TWILIO_SMS_TYPE,
  CUSTOM_SMS_TYPE
} from '../../util/channelTypes';
import {
  PHONE_REGEX,
  NO_ONLY_SPACES_REGEX
} from '../../util/validation/constants';
import { messages as TwilioAuth } from '../twilio-auth/messages';
import { AdvancedOptions } from './advanced-tab/messages';
import { BasicSetup } from './basic-setup-tab/messages';
import { twilioAccountSidRegex, twilioAuthTokenRegex } from './constants';
import {
  IntelepeerAuthMessages,
  GenesysSMSAuthMessages,
  CustomSMSAuthMessages
} from './sms-new/messages';

export const FIELDS = {
  INTELEPEER_ACCOUNT_SECRET: 'private.secrets.intelepeer_account_secret',
  INTELEPEER_AUTHENTICATION_TOKEN:
    'private.secrets.intelepeer_authentication_token',
  TWILIO_ACCOUNT_SID: 'private.secrets.twilio_account_sid',
  TWILIO_AUTHENTICATION_TOKEN: 'private.secrets.twilio_authentication_token',
  GENESYS_CLIENT_ID: 'private.secrets.genesys_client_id',
  GENESYS_CLIENT_SECRET: 'private.secrets.genesys_client_secret',
  BYOP_VERIFICATION_TOKEN: 'private.secrets.byop_verification_token',
  GENESYS_VERIFICATION_TOKEN: 'private.secrets.genesys_verification_token',
  CREATE_SESSION_VIA_INBOUND_MESSAGES: 'create_session_via_inbound_messages',
  MESSAGE_FAILURE_REPLY_MESSAGE: 'message_failure_reply_message',
  FROM_PHONE_NUMBER: 'private.from.phone_number',
  PROVIDER_TYPE: 'mobile_messaging_provider.type',
  PROVIDER_URL: 'mobile_messaging_provider.url',
  DESCRIPTION: 'description',
  WEBHOOK_URL: 'webhook_url'
};

export const SUBSCRIBE_PAYLOAD_TRANSFORM = {
  [FIELDS.DESCRIPTION]: 'private.description',
  [FIELDS.MESSAGE_FAILURE_REPLY_MESSAGE]:
    'private.error_handling.message_failure.reply_message',
  [FIELDS.PROVIDER_TYPE]: 'private.mobile_messaging_provider.type',
  [FIELDS.PROVIDER_URL]: 'private.mobile_messaging_provider.url',
  [FIELDS.CREATE_SESSION_VIA_INBOUND_MESSAGES]:
    'private.session.create_via_inbound_messages',
  [FIELDS.TWILIO_ACCOUNT_SID]: 'private.secrets.twilio_account_sid',
  [FIELDS.TWILIO_AUTHENTICATION_TOKEN]:
    'private.secrets.twilio_authentication_token',
  [FIELDS.WEBHOOK_URL]: 'private.secrets.webhook_url'
};

export const smsConfigSchema = yup.object().shape({
  private: yup.object().shape({
    description: yup.string().default(''),
    error_handling: yup.object().shape({
      messaging_failure: yup.object().shape({
        reply_message: yup
          .string()
          .required(
            AdvancedOptions.defaultMessagingFailureRequired.defaultMessage
          )
          .default(AdvancedOptions.defaultMessagingFailure.defaultMessage)
      })
    }),
    session: yup.object().shape({
      create_via_inbound_messages: yup.boolean().default(true)
    }),
    mobile_messaging_provider: yup.object().shape({
      type: yup.string().default(''), // either Twilio or IntelePeer or Genesys or BYOP
      url: yup
        .string()
        .url(AdvancedOptions.urlError.defaultMessage)
        .required(AdvancedOptions.urlRequired.defaultMessage) // either https://api.twilio.com OR https://api.intelepeer.com OR https://api.mypurecloud.com or https://api.cac1.pure.cloud
        .when(`../${FIELDS.PROVIDER_TYPE}`, {
          is: CUSTOM_SMS_TYPE,
          then: schema => schema.default(''),
          otherwise: schema =>
            schema.default(AdvancedOptions.urlDefault.defaultMessage)
        })
    }),
    secrets: yup.object().when(FIELDS.PROVIDER_TYPE, {
      is: TWILIO_SMS_TYPE,
      then: yup
        .object()
        .shape({
          twilio_account_sid: yup
            .string()
            .required(TwilioAuth.accountSidRequired)
            .matches(twilioAccountSidRegex, {
              message: TwilioAuth.accountSidError,
              excludeEmptyString: true
            }), // Twilio account associcated with this integration.,
          twilio_authentication_token: yup
            .string()
            .required(TwilioAuth.authTokenRequired)
            .matches(twilioAuthTokenRegex, {
              message: TwilioAuth.authTokenError,
              excludeEmptyString: true
            }) // Twilio authentication token used for submitting WhatsApp messages to Twilio.
        })
        .when(FIELDS.PROVIDER_TYPE, {
          is: INTELEPEER_SMS_TYPE,
          then: yup.object().shape({
            intelepeer_account_secret: yup
              .string()
              .min(8, IntelepeerAuthMessages.accountSecretMinCharError)
              .max(64, IntelepeerAuthMessages.accountSecretMaxCharError)
              .required(IntelepeerAuthMessages.accountSecretRequired)
              .matches(NO_ONLY_SPACES_REGEX, {
                message: IntelepeerAuthMessages.accountSecretNoWhitespaceError,
                excludeEmptyString: true
              }), // IntelePeer account associcated with this integration.,
            intelepeer_authentication_token: yup
              .string()
              .required(IntelepeerAuthMessages.authTokenRequired)
              .matches(NO_ONLY_SPACES_REGEX, {
                message: IntelepeerAuthMessages.authTokenNoWhitespaceError,
                excludeEmptyString: true
              }) // IntelePeer token used for submitting WhatsApp messages to Twilio.
          })
        })
        .when(FIELDS.PROVIDER_TYPE, {
          is: GENESYS_SMS_TYPE,
          then: yup.object().shape({
            genesys_client_id: yup
              .string()
              .required(GenesysSMSAuthMessages.clientIdRequired)
              .matches(NO_ONLY_SPACES_REGEX, {
                message: GenesysSMSAuthMessages.nonEmptyClientId,
                excludeEmptyString: true
              }), // Genesys account associcated with this integration.,
            genesys_client_secret: yup
              .string()
              .required(GenesysSMSAuthMessages.clientSecretRequired)
              .matches(NO_ONLY_SPACES_REGEX, {
                message: GenesysSMSAuthMessages.nonEmptyClientSecret,
                excludeEmptyString: true
              }), // Genesys account secret associcated with this integration.
            genesys_verification_token: yup
              .string()
              .required(GenesysSMSAuthMessages.verificationTokenRequired)
              .matches(NO_ONLY_SPACES_REGEX, {
                message: GenesysSMSAuthMessages.nonEmptyVerificationToken,
                excludeEmptyString: true
              }) // Genesys verification token secret associcated with this integration.
          })
        })
        .when(FIELDS.PROVIDER_TYPE, {
          is: CUSTOM_SMS_TYPE,
          then: yup.object().shape({
            byop_verification_token: yup
              .string()
              .required(CustomSMSAuthMessages.verificationTokenRequired)
              .matches(NO_ONLY_SPACES_REGEX, {
                message: CustomSMSAuthMessages.nonEmptyVerificationToken,
                excludeEmptyString: true
              })
          })
        })
    }),
    webhook_url: yup.string().ensure().required(),
    from: yup.object().shape({
      name: yup.string().nullable().default(null),
      phone_number: yup
        .string()
        .nullable()
        .default(null)
        .matches(PHONE_REGEX, {
          message: BasicSetup.phoneNumberError,
          excludeEmptyString: true
        })
        .matches(NO_ONLY_SPACES_REGEX, {
          message: BasicSetup.phoneNumberEmptyError,
          excludeEmptyString: true
        }),
      primary: yup.boolean().nullable().default(null)
    })
  })
});

export const settingsSchema = yup.object().shape({
  private: yup
    .object()
    .default(undefined)
    .when(FIELDS.PROVIDER_TYPE, {
      is: TWILIO_SMS_TYPE,
      then: yup
        .object()
        .shape({
          secrets: yup
            .object()
            .shape({
              twilio_account_sid: yup
                .string()
                .required(TwilioAuth.accountSidRequired)
                .matches(twilioAccountSidRegex, {
                  message: TwilioAuth.accountSidError,
                  excludeEmptyString: true
                }),
              twilio_authentication_token: yup
                .string()
                .required(TwilioAuth.authTokenRequired)
                .matches(twilioAuthTokenRegex, {
                  message: TwilioAuth.authTokenError,
                  excludeEmptyString: true
                })
            })
            .nullable()
            .default(undefined),
          from: yup
            .object()
            .shape({
              name: yup.string().default(undefined),
              phone_number: yup
                .string()
                .matches(PHONE_REGEX, {
                  message: BasicSetup.phoneNumberError,
                  excludeEmptyString: true
                })
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: BasicSetup.phoneNumberEmptyError,
                  excludeEmptyString: true
                })
                .nullable()
                .default(undefined)
            })
            .nullable()
            .default(undefined)
        })
        .nullable()
        .default(undefined)
    })
    .when(FIELDS.PROVIDER_TYPE, {
      is: INTELEPEER_SMS_TYPE,
      then: yup
        .object()
        .shape({
          secrets: yup
            .object()
            .shape({
              intelepeer_account_secret: yup
                .string()
                .min(8, IntelepeerAuthMessages.accountSecretMinCharError)
                .max(64, IntelepeerAuthMessages.accountSecretMaxCharError)
                .required(IntelepeerAuthMessages.accountSecretRequired)
                .matches(NO_ONLY_SPACES_REGEX, {
                  message:
                    IntelepeerAuthMessages.accountSecretNoWhitespaceError,
                  excludeEmptyString: true
                }),
              intelepeer_authentication_token: yup
                .string()
                .required(IntelepeerAuthMessages.authTokenRequired)
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: IntelepeerAuthMessages.authTokenNoWhitespaceError,
                  excludeEmptyString: true
                })
            })
            .nullable()
            .default(undefined),
          from: yup
            .object()
            .shape({
              name: yup.string(),
              phone_number: yup
                .string()
                .matches(PHONE_REGEX, {
                  message: BasicSetup.phoneNumberError,
                  excludeEmptyString: true
                })
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: BasicSetup.phoneNumberEmptyError,
                  excludeEmptyString: true
                })
                .nullable()
                .default(undefined)
            })
            .nullable()
            .default(undefined)
        })
        .nullable()
        .default(undefined)
    })
    .when(FIELDS.PROVIDER_TYPE, {
      is: GENESYS_SMS_TYPE,
      then: yup
        .object()
        .shape({
          secrets: yup
            .object()
            .shape({
              genesys_client_id: yup
                .string()
                .required(GenesysSMSAuthMessages.clientIdRequired)
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: GenesysSMSAuthMessages.nonEmptyClientId,
                  excludeEmptyString: true
                }), // Genesys account associcated with this integration.,
              genesys_client_secret: yup
                .string()
                .required(GenesysSMSAuthMessages.clientSecretRequired)
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: GenesysSMSAuthMessages.nonEmptyClientSecret,
                  excludeEmptyString: true
                }), // Genesys account secret associcated with this integration.
              genesys_verification_token: yup
                .string()
                .required(GenesysSMSAuthMessages.verificationTokenRequired)
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: GenesysSMSAuthMessages.nonEmptyVerificationToken,
                  excludeEmptyString: true
                }) // Genesys verification token secret associcated with this integration.
            })
            .nullable()
            .default(undefined),
          from: yup
            .object()
            .shape({
              name: yup.string(),
              phone_number: yup
                .string()
                .matches(PHONE_REGEX, {
                  message: BasicSetup.phoneNumberError,
                  excludeEmptyString: true
                })
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: BasicSetup.phoneNumberEmptyError,
                  excludeEmptyString: true
                })
                .nullable()
                .default(undefined)
            })
            .nullable()
            .default(undefined)
        })
        .nullable()
        .default(undefined)
    })
    .when(FIELDS.PROVIDER_TYPE, {
      is: CUSTOM_SMS_TYPE,
      then: yup
        .object()
        .shape({
          secrets: yup
            .object()
            .shape({
              byop_verification_token: yup
                .string()
                .required(CustomSMSAuthMessages.verificationTokenRequired)
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: CustomSMSAuthMessages.nonEmptyVerificationToken,
                  excludeEmptyString: true
                })
            })
            .nullable()
            .default(undefined),
          from: yup
            .object()
            .shape({
              name: yup.string(),
              phone_number: yup
                .string()
                .matches(PHONE_REGEX, {
                  message: BasicSetup.phoneNumberError,
                  excludeEmptyString: true
                })
                .matches(NO_ONLY_SPACES_REGEX, {
                  message: BasicSetup.phoneNumberEmptyError,
                  excludeEmptyString: true
                })
                .nullable()
                .default(undefined)
            })
            .nullable()
            .default(undefined)
        })
        .nullable()
        .default(undefined)
    })
    .nullable()
    .default(undefined),
  description: yup.string().default(''),
  webhook_url: yup.string().required(),
  mobile_messaging_provider: yup.object().shape({
    type: yup.string(),
    url: yup
      .string()
      .url(AdvancedOptions.urlError)
      .required(AdvancedOptions.urlRequired)
      .when(`../${FIELDS.PROVIDER_TYPE}`, {
        is: CUSTOM_SMS_TYPE,
        then: schema => schema.default(''),
        otherwise: schema =>
          schema.default(AdvancedOptions.urlDefault.defaultMessage)
      })
  }),
  create_session_via_inbound_messages: yup.boolean().default(true),
  message_failure_reply_message: yup
    .string()
    .default(AdvancedOptions.defaultMessagingFailure.defaultMessage),
  phone_numbers: yup.array().of(
    yup.object().shape({
      name: yup.string(),
      primary: yup.boolean().default(false),
      phone_number: yup.string()
    })
  )
});
